import { generatePath, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { AttrType, Campaign, CampaignEventType, CampaignTemplateRead, GraphRoot, ResourceState } from '@/api/dashboard'
import { dashboardClient, getErrorText } from '@/api'
import CampaignSelector, { CampaignItem } from '../components/CampaignSelector/CampaignSelector'
import { LIVEOPS_DETAILS_PATH, useGenerateCompanyPath } from '@/libs'
import { ButtonIcon, ToastSeverity, useToast } from '@/ui'
import { useQuery } from '@tanstack/react-query'
import { campaignsQuery, useGameSettingsQuery, useGameSettingsUpdateMutate } from '@/layouts/campaigns/api'
import { campaignTemplatesQuery } from '@/layouts/campaigns/api/campaignTemplatesQuery'
import { ChevronLeft, CloseOutline } from '@/icons'
import { CampaignSettings } from '@/layouts/campaigns/widgets/CampaignSettings'
import { Button, Modal, ModalProps, ModalTitle } from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { DATE_TIME_NOW_ATTRIBUTE_VALUE, EventActionNodeType } from '@/layouts/campaigns/types'
import { useGameItems } from '@/api/useGameItems'
import { CONTEXT_ITEM } from '@/layouts/campaigns/editors/common'
import { getCampaignIconUrl } from '@/layouts/campaigns/util'
import { SelectorValueType } from '@/expr/types'
import { findAttr } from '@/expr/findAttr'

export default function CreateCampaignModal(props: ModalProps) {
  const { ...rest } = props
  const navigate = useNavigate()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { t } = useTranslation()
  const showToast = useToast()
  const [step, setStep] = useState<number>(0)

  const { data: settings } = useGameSettingsQuery(companyId, gameId)
  const { items = [] } = useGameItems()
  const { mutateAsync: updateMutateAsync } = useGameSettingsUpdateMutate(companyId, gameId)
  const { data: createdCampaigns = [] } = useQuery(
    campaignsQuery(companyId, gameId, { resource_state: ResourceState.Active }),
  )
  const { data: templates = [] } = useQuery(campaignTemplatesQuery(companyId, gameId))

  const [newCampaign, setNewCampaign] = useState<Campaign>({
    type: CampaignEventType.CustomEvent,
    name: '',
    id: '',
    enabled: false,
    attributes: {
      custom_attributes: [],
    },
    version: '0.0.1',
    version_description: 'Initial version',
    created_at: new Date().getTime() / 1000,
  })

  const saveClick = async (c: Campaign) => {
    try {
      c.time_limit = !!(c.start_at || c.end_at)
      let { data: created } = await dashboardClient.v1.createCampaign(companyId, gameId, c)
      navigate(generatePath(LIVEOPS_DETAILS_PATH, { companyId, gameId, eventId: created.id }))
    } catch (e) {
      showToast({ message: t(`server-errors.${getErrorText(e)}`), severity: ToastSeverity.error })
    }
  }

  const updateItemIds = (graph: GraphRoot) => {
    let itemIdx = 0
    const getNextItem = () => {
      if (items.length == 0) {
        return null
      }

      if (itemIdx >= items.length) {
        itemIdx = 0
      }
      return items[itemIdx++].id
    }

    for (let id in graph.nodes) {
      let node = graph.nodes[id]

      switch (node.model_type) {
        case EventActionNodeType.ItemBonusOfferActionNode:
        case EventActionNodeType.ItemDiscountOfferActionNode:
        case EventActionNodeType.ItemOfferActionNode:
        case EventActionNodeType.CreateVirtualSKUNode:
        case EventActionNodeType.CreateCouponNode:
          if (node.item_id != CONTEXT_ITEM) {
            node.item_id = getNextItem()
          }
          break
        case EventActionNodeType.CreateItemBannerActionNode:
          if (node.config.featured_item_id != CONTEXT_ITEM) {
            node.config.featured_item_id = getNextItem() as string
          }
          node.page_id = null as unknown as string
          break
        case EventActionNodeType.ConditionNode:
          const allTokens = node.expr?.tokens || []

          for (let i = 0; i < allTokens.length; i += 4) {
            const attrToken = allTokens[i]
            const valToken = allTokens[i + 2]

            const attr = findAttr(attrToken.value)
            if (!attr) {
              continue
            }

            if (
              attr.selectorValueType == SelectorValueType.sku ||
              attr.selectorValueType == SelectorValueType.store_item ||
              attr.selectorValueType == SelectorValueType.segment ||
              attr.selectorValueType == SelectorValueType.sku_no_price ||
              attr.selectorValueType == SelectorValueType.skus
            ) {
              valToken.value = getNextItem() as string
            }
          }

          break
      }
    }
  }

  const updateAttributesFromTemplate = async (template: CampaignTemplateRead) => {
    if (!settings?.id) {
      return
    }

    const newAttributes = [...(settings?.player_attributes || [])]

    for (const nodeId in template.graph.nodes) {
      const node = template.graph.nodes[nodeId]
      if (node.model_type === EventActionNodeType.SetAttributeValuesNode && node.values) {
        for (const attrName in node.values) {
          let attr = newAttributes.find(attr => attrName === attr.name)
          if (attr) {
            continue
          }

          const value = node.values[attrName]
          let type = AttrType.String

          if (value == DATE_TIME_NOW_ATTRIBUTE_VALUE) {
            type = AttrType.Date
          } else if (typeof value == 'number') {
            type = AttrType.Number
          }

          newAttributes.push({
            name: attrName,
            type: type,
          })
        }
      }
    }

    await updateMutateAsync({
      settingsId: settings.id,
      data: { ...settings, player_attributes: newAttributes },
    })
  }

  const onTemplateSelected = async (template: CampaignTemplateRead) => {
    let { data: created } = await dashboardClient.v1.createCampaign(companyId, gameId, {
      ...newCampaign,
      type: template.type,
    })

    await updateAttributesFromTemplate(template)
    updateItemIds(template.graph)

    navigate(generatePath(LIVEOPS_DETAILS_PATH, { companyId, gameId, eventId: created.id }), {
      state: { graph: template.graph },
    })
  }

  const backClick = () => {
    if (step == 3) {
      setStep(0)
    } else {
      setStep(step - 1)
    }
  }

  const renderTemplateList = () => {
    const campaignByType = {} as Record<CampaignEventType, CampaignTemplateRead[]>

    for (const template of templates) {
      if (!campaignByType[template.type]) {
        campaignByType[template.type] = []
      }
      campaignByType[template.type].push(template)
    }

    const sortedKeys = Object.keys(campaignByType).sort((a, b) => a.localeCompare(b)) as CampaignEventType[]

    return (
      <div className="flex flex-col gap-[20px]">
        {sortedKeys.map((key: CampaignEventType) => (
          <>
            <div>
              <p className="text-[18px] font-semibold text-text-tertiary">{t(`campaign.type.${key}`)}</p>
              <span className="text-text-secondary">{t(`campaign.type.desc.${key}`)}</span>
            </div>
            <div className="flex flex-wrap gap-[42px]">
              {(campaignByType[key] as CampaignTemplateRead[]).map(it => (
                <div className="flex flex-col gap-2">
                  <CampaignItem
                    key={it.id}
                    buttonText={t('campaign.use-template')}
                    onNewClick={() => onTemplateSelected(it)}
                    iconUrl={getCampaignIconUrl(it.type)}
                  />
                  <div className="w-[180px] text-text-secondary">
                    {it.description || t(`campaign.type.desc.${key}`)}
                  </div>
                </div>
              ))}
            </div>
          </>
        ))}
      </div>
    )
  }

  const getStepName = () => {
    switch (step) {
      case 0:
        return t('campaign.step1')
      case 1:
        return t('campaign.step2')
      case 2:
        return t('campaign.step3')
    }
    return ''
  }

  const onEventTypeSelected = (type: CampaignEventType) => {
    setNewCampaign({
      ...newCampaign,
      type,
    })
    switch (type) {
      case CampaignEventType.GameHubVisit:
      case CampaignEventType.GameHubVisitStore:
        if (!newCampaign.attributes?.visit_duration) {
          setNewCampaign(prev => ({
            ...prev,
            attributes: {
              visit_duration: 60 * 60 * 24,
            },
          }))
        }
        break
      case CampaignEventType.AbandonedCart:
        if (!newCampaign.attributes?.abandoned_cart_duration) {
          setNewCampaign(prev => ({
            ...prev,
            attributes: {
              abandoned_cart_duration: 60 * 10,
            },
          }))
        }
        break
    }
    setStep(3)
  }

  const renderModalTitle = () => {
    return (
      <ModalTitle className="w-full">
        <div className="flex items-center justify-between">
          <div>
            {step > 0 && (
              <ButtonIcon variant="outline" color="secondary" size="xs" onClick={backClick} className="mr-6">
                <ChevronLeft size={16} />
              </ButtonIcon>
            )}
            {getStepName()}
          </div>

          <div className="flex items-center gap-2">
            {templates.length > 0 && step == 0 && (
              <Button onClick={() => setStep(1)} variant="outline">
                {t('campaign.type.create-from-template')}
              </Button>
            )}
            <ButtonIcon variant="outline" color="secondary" size="sm" onClick={props.onClose}>
              <CloseOutline size={16} />
            </ButtonIcon>
          </div>
        </div>
      </ModalTitle>
    )
  }

  return (
    <Modal {...rest} className="flex h-[90vh] max-w-[1144px] flex-col px-0">
      {step < 2 && (
        <>
          {renderModalTitle()}
          <section className="w-full overflow-y-auto px-6 pb-6">
            {step == 0 && <CampaignSelector createdCampaigns={createdCampaigns} onNewClick={onEventTypeSelected} />}
            {step == 1 && renderTemplateList()}
          </section>
        </>
      )}

      {step > 1 && (
        <CampaignSettings
          backClick={backClick}
          onClose={props.onClose!}
          campaign={newCampaign}
          setCampaign={c => saveClick(c)}
        />
      )}
    </Modal>
  )
}
